import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { PricesContainer, DualPriceContainer } from './PriceBreakdown.styles';
import { selectPriceBreakdowns, PriceBreakdownType } from '../../../redux/priceBreakdownReducer';
import PriceLine from './PriceLine';
import PriceItem from './PriceItem';
import { PricesPriorityOrder, Rebate, UsedPricesPriorityOrder } from '../../../types';
import { parseConfigs } from '../../../ContentMapping/cleanConfigs';
import { CarPricesType } from '../../../types/oneGraphTypes';
import {
  shouldShowFinalPrice,
  shouldCheckMainPrice,
  getVisiblePricesCount,
} from '../../../utils/priceUtils';
import { formatPrice } from '../../../utils/currencyTools';

interface PriceBreakdownProps {
  typedPrices: CarPricesType[];
  rebate: Rebate[] | null;
  onModalOpen: (e: any, title: string, text: string) => void;
  isNewVehicle?: boolean;
}

const PriceBreakdown: React.FC<PriceBreakdownProps> = ({
  typedPrices,
  rebate,
  onModalOpen,
  isNewVehicle,
}) => {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? 'en';
  const isFrench = language === 'fr';
  // use this for author entered price breakdown settings
  const rawPriceBreakdownGroup = useSelector(selectPriceBreakdowns)
    ?.priceBreakdownGroup as PriceBreakdownType;
  const priceBreakdownGroup = useMemo(() => {
    return parseConfigs(rawPriceBreakdownGroup, isNewVehicle || false);
  }, [isNewVehicle, rawPriceBreakdownGroup]);
  const displayRebates = useSelector(selectPriceBreakdowns)?.displayRebates || false;
  const displayRebatesUsed = useSelector(selectPriceBreakdowns)?.displayRebatesUsed || false;
  const negativePricesNew = useSelector(selectPriceBreakdowns)?.negativePricesNew;
  const negativePricesUsed = useSelector(selectPriceBreakdowns)?.negativePricesUsed;
  const negativePriceKeys = isNewVehicle ? negativePricesNew : negativePricesUsed;
  const overrideCopyDefault = isNewVehicle
    ? // NEW (Arriving Soon)
      t('vlp.vehicleResults.vehicleCard.textOverrideDefault')
    : // Pre-Owned (Call for price)
      t('vlp.vehicleResults.vehicleCard.textOverrideDefaultUsed');

  const showFinalPrice = useMemo(() => {
    return shouldShowFinalPrice(priceBreakdownGroup, typedPrices);
  }, [priceBreakdownGroup, typedPrices]);

  const checkMainPrice = useCallback(
    (priceKey: string) => {
      return shouldCheckMainPrice(
        priceKey,
        priceBreakdownGroup,
        !isNewVehicle,
        showFinalPrice,
        typedPrices,
      );
    },
    [isNewVehicle, priceBreakdownGroup, showFinalPrice, typedPrices],
  );

  const handleOpenModal = useCallback(
    (e: any, legalText: string) => {
      if (!legalText.length) return;
      const modalTitle = 'Legal Disclaimer';
      const modalText = legalText;
      onModalOpen(e, modalTitle, modalText);
    },
    [onModalOpen],
  );

  const countEnabledKeys = useMemo(() => {
    if (!priceBreakdownGroup || !typedPrices) return 0;
    return getVisiblePricesCount(
      priceBreakdownGroup,
      !isNewVehicle,
      displayRebates,
      displayRebatesUsed,
      rebate?.length ?? 0,
      typedPrices,
      showFinalPrice,
    );
  }, [
    displayRebates,
    displayRebatesUsed,
    isNewVehicle,
    priceBreakdownGroup,
    rebate?.length,
    showFinalPrice,
    typedPrices,
  ]);

  const renderMultiplePrices = useMemo(() => {
    if (!priceBreakdownGroup) return null;
    // sort price options for used vehicles are different from new vehicles
    const pricesPriority = !isNewVehicle ? UsedPricesPriorityOrder : PricesPriorityOrder;
    const sortedBreakdown = Object.entries(priceBreakdownGroup)
      // Filter out Final Price because we show final price at the end of price rows after rebates
      .filter(([key, value]) => value.isEnabled && key !== 'final')
      .map(([key, value]) => ({ key, value }))
      .sort((a, b) => {
        const priorityA = pricesPriority.indexOf(a.key);
        const priorityB = pricesPriority.indexOf(b.key);
        return priorityA - priorityB;
      });
    if (!sortedBreakdown?.length) return null;

    return sortedBreakdown?.map((item) => {
      const priceConfig = item.value;
      const { priceKey } = priceConfig;
      if (!priceConfig?.isEnabled) return null;
      const priceAmount = typedPrices.find((element) => element?.type === priceKey);
      const isOverrideEnabled = priceConfig?.hasTextOverride;
      const hasNoAmount = !priceAmount || !priceAmount?.price?.value;
      // if there is no data for this price field and text override is not enabled in CFM then hide this price row
      if (hasNoAmount && !isOverrideEnabled) return null;
      const regularPrice = typedPrices.find((element) => element?.type === 'list');
      const regularPriceAmount = regularPrice?.price?.value;
      // TEMPORARILY LOGIC for NARSHOP-1164
      // if absolute value of dealer discount is equal to regular price (msrp) then hide dealer discount row
      if (
        priceKey === 'dealerDiscount' &&
        priceAmount?.price?.value &&
        regularPriceAmount === Math.abs(priceAmount?.price?.value) &&
        priceAmount?.price?.value < 0
      ) {
        return null;
      }
      const isMainPrice = checkMainPrice(priceKey);
      const textOverrideCopy = priceConfig?.textOverride || overrideCopyDefault;
      const shouldBeNegative = negativePriceKeys?.includes(priceKey);

      if (countEnabledKeys <= 2) {
        return (
          <div key={priceKey}>
            <PriceItem
              priceValue={
                hasNoAmount
                  ? textOverrideCopy
                  : formatPrice(priceAmount?.price?.value, isFrench, shouldBeNegative)
              }
              label={priceConfig.label || ''}
              longDisclaimer={priceConfig?.longDisclaimer || ''}
              shortDisclaimer={priceConfig?.shortDisclaimer || ''}
              priceKey={priceKey}
              isBold={isMainPrice}
              handleOpenModal={handleOpenModal}
            />
          </div>
        );
      }

      return (
        <PriceLine
          priceValue={
            hasNoAmount
              ? textOverrideCopy
              : formatPrice(priceAmount?.price?.value, isFrench, shouldBeNegative)
          }
          label={priceConfig.label || ''}
          longDisclaimer={priceConfig?.longDisclaimer || ''}
          shortDisclaimer={priceConfig?.shortDisclaimer || ''}
          priceKey={priceKey}
          key={priceKey}
          handleOpenModal={handleOpenModal}
          mainPrice={isMainPrice}
        />
      );
    });
  }, [
    checkMainPrice,
    countEnabledKeys,
    handleOpenModal,
    isFrench,
    isNewVehicle,
    negativePriceKeys,
    overrideCopyDefault,
    priceBreakdownGroup,
    typedPrices,
  ]);

  const renderRebates = useMemo(() => {
    if (!displayRebates || !rebate?.length) return null;
    if (!isNewVehicle && !displayRebatesUsed) return null;

    return rebate?.map((item, index) => {
      const priceDetail = item.Value;
      const priceKey = `rebate-${index}`;
      // If for a rebate, offerName or price value is empty do not show that rebate at all
      if (!priceDetail || !item.Name) return null;

      if (countEnabledKeys <= 2) {
        return (
          <div key={priceKey}>
            <PriceItem
              priceValue={priceDetail ? formatPrice(priceDetail, isFrench, true) : '—'}
              label={item?.Name || ''}
              longDisclaimer={item?.offerDescription || ''}
              shortDisclaimer=""
              priceKey={priceKey}
              isBold={false}
              handleOpenModal={handleOpenModal}
            />
          </div>
        );
      }
      return (
        <PriceLine
          priceValue={priceDetail ? formatPrice(priceDetail, isFrench, true) : '—'}
          label={item?.Name || ''}
          longDisclaimer={item?.offerDescription || ''}
          shortDisclaimer=""
          priceKey={priceKey}
          key={priceKey}
          handleOpenModal={handleOpenModal}
          mainPrice={false}
        />
      );
    });
  }, [
    countEnabledKeys,
    displayRebates,
    displayRebatesUsed,
    handleOpenModal,
    isFrench,
    isNewVehicle,
    rebate,
  ]);

  const renderFinalPrice = useMemo(() => {
    if (!priceBreakdownGroup) return null;

    const finalPriceConfig = priceBreakdownGroup?.final;
    if (!finalPriceConfig?.isEnabled || !showFinalPrice) return null;
    const { priceKey } = finalPriceConfig;

    const priceAmount = typedPrices.find((element) => element?.type === priceKey);
    if (!priceAmount?.price?.value && !finalPriceConfig?.hasTextOverride) return null;
    const isMainPrice = true;
    const textOverrideCopy = finalPriceConfig?.textOverride || overrideCopyDefault;

    if (countEnabledKeys <= 2) {
      return (
        <div key={priceKey}>
          <PriceItem
            priceValue={
              priceAmount?.price?.value
                ? formatPrice(priceAmount?.price?.value, isFrench)
                : textOverrideCopy
            }
            label={finalPriceConfig.label || ''}
            longDisclaimer={finalPriceConfig?.longDisclaimer || ''}
            shortDisclaimer={finalPriceConfig?.shortDisclaimer || ''}
            priceKey={priceKey}
            isBold={isMainPrice}
            handleOpenModal={handleOpenModal}
          />
        </div>
      );
    }

    return (
      <PriceLine
        priceValue={
          priceAmount?.price?.value
            ? formatPrice(priceAmount?.price?.value, isFrench)
            : textOverrideCopy
        }
        label={finalPriceConfig.label || ''}
        longDisclaimer={finalPriceConfig?.longDisclaimer || ''}
        shortDisclaimer={finalPriceConfig?.shortDisclaimer || ''}
        priceKey={priceKey}
        key={priceKey}
        handleOpenModal={handleOpenModal}
        mainPrice={isMainPrice}
      />
    );
  }, [
    countEnabledKeys,
    handleOpenModal,
    isFrench,
    overrideCopyDefault,
    priceBreakdownGroup,
    showFinalPrice,
    typedPrices,
  ]);

  // If less than two price is enabled
  if (countEnabledKeys <= 2)
    return (
      <DualPriceContainer>
        {renderMultiplePrices}
        {renderRebates}
        {renderFinalPrice}
      </DualPriceContainer>
    );

  return (
    <PricesContainer>
      {renderMultiplePrices}
      {renderRebates}
      {renderFinalPrice}
    </PricesContainer>
  );
};

export default PriceBreakdown;
